<template>
    <!-- Un glyphe (emoji) plutôt qu'une icône du catalogue. Il passe par le
         MÊME point d'entrée pour que n'importe quel appelant qui prend un
         `name` d'icône, `IconTile` et `AppModalHeader` compris, l'accepte sans
         rien changer. Sans ça, changer un symbole pour un emoji obligerait à
         ouvrir chaque coquille du système. -->
    <span
        v-if="glyph"
        :class="iconClass"
        :style="{ fontSize: Math.round(size * 0.95) + 'px', lineHeight: 1 }"
        role="img"
        v-bind="$attrs"
    >{{ glyph }}</span>
    <component 
        v-else-if="iconComponent" 
        :is="iconComponent" 
        :size="size"
        :class="iconClass"
        v-bind="$attrs"
    />
    <component 
        v-else-if="fallbackIcon" 
        :is="fallbackIcon" 
        :size="size"
        :class="iconClass"
        v-bind="$attrs"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RefreshCw, Sparkles, HelpCircle } from 'lucide-vue-next';
import { getIconComponent } from '@/services/icon.service';

interface Props {
    /** Nom de l'icône (peut être au format "lucide:icon-name", "icon-name" ou "IconName") */
    name?: string | null;
    /** Taille de l'icône en pixels */
    size?: number;
    /** Classes CSS additionnelles */
    class?: string;
    /** Icône de fallback si l'icône n'est pas trouvée */
    fallback?: 'refresh' | 'sparkles' | 'help' | null;
}

const props = withDefaults(defineProps<Props>(), {
    name: null,
    size: 24,
    class: '',
    fallback: null,
});

// Fonction pour extraire le nom d'icône du format "lucide:icon-name" ou retourner le nom tel quel
function getIconNameFromValue(value: string | null | undefined): string | null {
    if (!value) return null;
    if (value.startsWith('lucide:')) {
        return value.replace('lucide:', '');
    }
    return value;
}

// Fonction pour convertir kebab-case en PascalCase
function kebabToPascalCase(kebab: string): string {
    return kebab.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join('');
}

/**
 * Un nom d'icône du catalogue s'écrit en ASCII (`kebab-case`, `PascalCase`, ou
 * préfixé `lucide:`). Tout le reste est un caractère à afficher tel quel :
 * c'est le test le plus étroit possible, il ne peut pas avaler un slug valide.
 */
const glyph = computed(() => {
    const name = props.name?.trim();
    if (!name) return null;
    return /^[A-Za-z][A-Za-z0-9:_-]*$/.test(name) ? null : name;
});

// Récupération du composant d'icône
const iconComponent = computed(() => {
    if (glyph.value) return null;
    if (!props.name) return null;
    
    // Extraire le nom de l'icône (gérer le format "lucide:icon-name")
    const iconName = getIconNameFromValue(props.name);
    if (!iconName) return null;
    
    // Convertir en PascalCase si nécessaire (pour les noms en kebab-case)
    const pascalCaseName = kebabToPascalCase(iconName);
    
    // Essayer d'abord avec le nom en PascalCase
    let component = getIconComponent(pascalCaseName);
    if (component) {
        return component;
    }
    
    // Si ça ne fonctionne pas, essayer avec le nom original (au cas où il est déjà en PascalCase)
    component = getIconComponent(iconName);
    if (component) {
        return component;
    }
    
    return null;
});

// Icône de fallback
const fallbackIcon = computed(() => {
    if (glyph.value || iconComponent.value) return null;
    
    switch (props.fallback) {
        case 'refresh':
            return RefreshCw;
        case 'sparkles':
            return Sparkles;
        case 'help':
            return HelpCircle;
        default:
            return null;
    }
});

// Classes CSS pour l'icône
const iconClass = computed(() => {
    return props.class;
});
</script>

